import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { Skeleton, TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import { alertsSelectorAdd } from '../../recoil';
import { defaultApi } from '../../apis';
import IbButton from '../common/IbButton';
import IbModal, { IbModalPosition } from '../common/IbModal';
import { ConnectorModel } from '../../../gamma-api';
import IbTypography from '../common/IbTypography';
import IbTable from '../common/IbTable';
import IbIcon from '../common/IbIcon';
import IbPagination from '../common/IbPagination';
import IbDebounceInput from '../common/IbDebounceInput';
import IbConfirmModal from '../common/IbConfirmModal';
import IbTextHighlighter from '../common/IbTextHighlighter';
import { getTranslateFunc } from '../../utils/stringUtil';
import { ConnectorsEmptyLogo } from '../../assets';
import IbContextMenu, { IIbContextMenuItem } from '../common/IbContextMenu';
import ConnectorModal from '../ConnectorModal';

import {
  CONNECTOR_PAGE_SIZE,
  CONTENT_CLASS_NAME,
  PLACEHOLDER_CLASS_NAME,
  MAIN_CLASS_NAME,
  SEARCH_CLASS_NAME,
  BUTTONS_CLASS_NAME,
} from './const';

interface IConnectorsModalProps {
  visible: boolean;
  onClose?: () => void;
}

const ConnectorsModal: React.FC<IConnectorsModalProps> = ({ visible, onClose }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: CONNECTOR_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [connectors, setConnectors] = useState<ConnectorModel[]>([]);

  const [formConnector, setFormConnector] = useState<ConnectorModel>();
  const [contextConnector, setContextConnector] = useState<ConnectorModel>();
  const [deleteConnector, setDeleteConnector] = useState<ConnectorModel>();

  const [dataLoading, setDataLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formModalVisible, setFormModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const translate = getTranslateFunc('Connectors: Modal:');

  const loadConnectors = async () => {
    try {
      const response = await defaultApi.searchConnectors(
        (pagination.current || 1) - 1,
        CONNECTOR_PAGE_SIZE,
        true,
        searchText
      );
      setConnectors(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onInitLoadingAsync = async () => {
    setDataLoading(true);
    await loadConnectors();
    setDataLoading(false);
  };

  const onInitLoadingSync = () => {
    onInitLoadingAsync().finally();
  };

  const onPageChangeAsync = async () => {
    setPageLoading(true);
    await loadConnectors();
    setPageLoading(false);
  };

  const onPageChangeSync = () => {
    onPageChangeAsync().finally();
  };

  useEffect(() => {
    visible && onInitLoadingSync();
  }, [visible]);

  useEffect(() => {
    onPageChangeSync();
  }, [pagination.current, pagination.pageSize, searchText]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [visible, searchText]);

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onAddButtonClick = () => {
    setFormModalVisible(true);
    setFormConnector(undefined);
  };

  const onFormModalClose = () => {
    setFormModalVisible(false);
    setFormConnector(undefined);
  };

  const onContextOpenClick = (connector: ConnectorModel) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    setContextConnector(connector);
  };

  const onContextCloseClick = () => {
    setContextConnector(undefined);
  };

  const onContextUpdateClick = (connector: ConnectorModel) => () => {
    setFormConnector(connector);
    setFormModalVisible(true);
  };

  const onContextDeleteClick = (connector: ConnectorModel) => () => {
    setDeleteConnector(connector);
  };

  const onConnectorCreate = async (connector: ConnectorModel) => {
    setSaving(true);

    try {
      await defaultApi.createConnector(connector);
    } catch (e) {
      addAlert({
        content: translate('Creation error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      setSaving(false);
      return;
    }

    setFormModalVisible(false);
    setFormConnector(undefined);
    setSaving(false);

    setPageLoading(true);
    await loadConnectors();
    setPageLoading(false);
  };

  const onConnectorUpdate = async (connector: ConnectorModel) => {
    setSaving(true);

    try {
      await defaultApi.updateConnector(connector.connector_id, connector);
    } catch (e) {
      addAlert({
        content: translate('Updating error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      setSaving(false);
      return;
    }

    setFormModalVisible(false);
    setFormConnector(undefined);
    setSaving(false);

    setPageLoading(true);
    await loadConnectors();
    setPageLoading(false);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteConnector) {
      return;
    }

    setSaving(true);
    setDeleteConfirmLoading(true);

    try {
      await defaultApi.deleteConnector(deleteConnector.connector_id);
    } catch (e) {
      addAlert({
        content: translate('Deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      setDeleteConfirmLoading(false);
      setSaving(false);
      return;
    }

    setDeleteConfirmLoading(false);
    setDeleteConnector(undefined);
    setSaving(false);

    setPageLoading(true);
    await loadConnectors();
    setPageLoading(false);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteConnector(undefined);
  };

  const renderSearch = () => {
    return (
      <div className={SEARCH_CLASS_NAME}>
        <IbDebounceInput
          allowClear
          placeholder={translate('Search: Placeholder')}
          prefix={<IbIcon iconName="search" />}
          onDebouncedChange={onSearchTextChange}
        />
      </div>
    );
  };

  const renderTable = () => {
    const menuItems = (connector: ConnectorModel): IIbContextMenuItem[] => [
      {
        icon: <IbIcon iconName="edit" />,
        text: translate('Table: Context: Update'),
        onSelect: onContextUpdateClick(connector),
      },
      {
        icon: <IbIcon iconName="delete" />,
        text: translate('Table: Context: Delete'),
        onSelect: onContextDeleteClick(connector),
      },
    ];

    const columns: ColumnType<ConnectorModel>[] = [
      {
        title: translate('Table: Name'),
        width: '35%',
        render: (_, connector) => (
          <IbTypography.Paragraph type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={connector.name} />
          </IbTypography.Paragraph>
        ),
      },
      {
        title: translate('Table: Auth'),
        width: '25%',
        render: (_, connector) => (
          <IbTypography.Paragraph type="secondary">
            {translate(`Table: Auth: ${connector.settings.apis.web.auth.type}`)}
          </IbTypography.Paragraph>
        ),
      },
      {
        title: translate('Table: URL'),
        width: '45%',
        render: (_, connector) => (
          <IbTypography.Paragraph type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={connector.settings.apis.web.url} />
          </IbTypography.Paragraph>
        ),
      },
      {
        title: '',
        render: (_, connector) => (
          <IbContextMenu
            menuItems={menuItems(connector)}
            visible={contextConnector?.connector_id === connector.connector_id}
            onClose={onContextCloseClick}
          >
            <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onContextOpenClick(connector)} />
          </IbContextMenu>
        ),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={connectors}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={pageLoading}
        rowKey={(connector: ConnectorModel) => connector.connector_id}
        size="small"
      />
    );
  };

  const renderPagination = () => {
    if ((pagination.total ?? 0) <= CONNECTOR_PAGE_SIZE) {
      return null;
    }

    return (
      <IbPagination
        current={pagination.current}
        defaultCurrent={1}
        defaultPageSize={CONNECTOR_PAGE_SIZE}
        pageSize={CONNECTOR_PAGE_SIZE}
        showSizeChanger={false}
        total={pagination.total}
        onChange={onPaginationChange}
      />
    );
  };

  const renderModals = () => {
    return (
      <ConnectorModal
        formConnector={formConnector}
        visible={formModalVisible}
        onCancel={onFormModalClose}
        onCreate={onConnectorCreate}
        onUpdate={onConnectorUpdate}
      />
    );
  };

  const renderConfirms = () => {
    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onDeleteConfirmButtonClick}>
              {translate('Delete confirm modal: Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onDeleteCancelButtonClick}>
              {translate('Delete confirm modal: Cancel')}
            </IbButton>
          </>
        }
        loading={deleteConfirmLoading}
        title={translate('Delete confirm modal: Title')}
        visible={!!deleteConnector}
        onCancel={onDeleteCancelButtonClick}
      >
        <IbTypography.Paragraph type="secondary">{translate('Delete confirm modal: Content')}</IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  const renderPlaceholder = () => {
    return (
      <div className={PLACEHOLDER_CLASS_NAME}>
        <ConnectorsEmptyLogo />
        <IbTypography>
          <IbTypography.Paragraph disabled type="secondary">
            {translate('Empty placeholder: line 1')}
            <br />
            {translate('Empty placeholder: line 2')}
            <br />
            {translate('Empty placeholder: line 3')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbButton icon={<IbIcon iconName="add-one" />} onClick={onAddButtonClick}>
          {translate('Create')}
        </IbButton>
      </div>
    );
  };

  const renderSkeleton = () => {
    return (
      <div>
        <Skeleton active />
        <Skeleton active />
      </div>
    );
  };

  const renderContentDefault = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        {renderSearch()}
        {renderTable()}
        {renderPagination()}
        {renderModals()}
        {renderConfirms()}
      </div>
    );
  };

  const renderContentPlaceholder = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        {renderPlaceholder()}
        {renderModals()}
      </div>
    );
  };

  const renderContentSkeleton = () => {
    return <div className={CONTENT_CLASS_NAME}>{renderSkeleton()}</div>;
  };

  const renderContent = () => {
    if (dataLoading) {
      return renderContentSkeleton();
    }

    if (!searchText && !pageLoading && !connectors.length) {
      return renderContentPlaceholder();
    }

    return renderContentDefault();
  };

  const renderFooter = () => {
    if (dataLoading || (!searchText && !pageLoading && !connectors.length)) {
      return null;
    }

    return (
      <div className={BUTTONS_CLASS_NAME}>
        <IbButton type="primary" onClick={onClose}>
          {translate('Close')}
        </IbButton>
        <IbButton icon={<IbIcon iconName="add-one" />} type="secondary" onClick={onAddButtonClick}>
          {translate('Create')}
        </IbButton>
      </div>
    );
  };

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={translate('Title')}
      visible={visible}
      onCancel={onClose}
    >
      {renderContent()}
    </IbModal>
  );
};

export default ConnectorsModal;
